import Text from "@src/lib/ui/Text";
import * as React from "react";

import { cn } from "@src/lib/utils/cn";
import { VariantProps, cva } from "class-variance-authority";

/**
 * Card: a white surface that groups related content, composed from three
 * axes — surface (edge), radius and elevation. The legacy `variant` prop maps
 * onto those axes (LEGACY_VARIANTS); any axis passed explicitly wins.
 */
export const cardVariants = cva("relative text-text-primary", {
  variants: {
    surface: {
      subtle: "border border-border-primary bg-bg-primary",
      strong: "border border-border-secondary bg-bg-primary",
      container: "border-0 bg-bg-primary",
      // Legacy: the builder-style canvas panel. Not a redesign option.
      "dotted-grid": "border border-border-primary bg-dotted-grid",
    },
    radius: {
      md: "rounded-md",
      lg: "rounded-lg",
      xl: "rounded-xl",
      "2xl": "rounded-2xl",
      // Legacy: the pre-redesign 24px radius, kept for dotted-grid panels.
      "3xl": "rounded-3xl",
    },
    elevation: {
      elevated: "shadow-default",
      flat: "shadow-none",
    },
  },
  defaultVariants: {
    surface: "subtle",
    radius: "xl",
    elevation: "flat",
  },
});

type CardAxes = Pick<
  NonNullable<VariantProps<typeof cardVariants>>,
  "surface" | "radius" | "elevation"
>;

export type CardVariant = "default" | "elevated" | "dotted-grid" | "soft";

/** How each pre-redesign variant maps onto the new axes. */
export const LEGACY_VARIANTS: Record<CardVariant, CardAxes> = {
  default: { surface: "subtle", radius: "xl", elevation: "flat" },
  elevated: { surface: "subtle", radius: "xl", elevation: "elevated" },
  "dotted-grid": {
    surface: "dotted-grid",
    radius: "3xl",
    elevation: "elevated",
  },
  soft: { surface: "container", radius: "lg", elevation: "elevated" },
};

export interface CardProps
  extends React.HTMLAttributes<HTMLDivElement>,
    CardAxes {
  /** Legacy preset; prefer surface / radius / elevation. */
  variant?: CardVariant | null;
}

const Card = React.forwardRef<HTMLDivElement, CardProps>(
  ({ variant, surface, radius, elevation, className, ...props }, ref) => {
    const legacy = variant ? LEGACY_VARIANTS[variant] : undefined;
    return (
      <div
        ref={ref}
        className={cn(
          cardVariants({
            surface: surface ?? legacy?.surface,
            radius: radius ?? legacy?.radius,
            elevation: elevation ?? legacy?.elevation,
          }),
          className,
        )}
        {...props}
      />
    );
  },
);
Card.displayName = "Card";

const CardHeader = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn("flex flex-col gap-1 px-6 pt-6 pb-4", className)}
    {...props}
  />
));
CardHeader.displayName = "CardHeader";

const CardTitle = React.forwardRef<
  HTMLParagraphElement,
  React.HTMLAttributes<HTMLHeadingElement>
>(({ children, className, ...props }, ref) => (
  <Text
    variant="body-title"
    ref={ref}
    className={cn("text-text-primary", className)}
    {...props}
  >
    {children}
  </Text>
));
CardTitle.displayName = "CardTitle";

const CardDescription = React.forwardRef<
  HTMLParagraphElement,
  React.HTMLAttributes<HTMLParagraphElement>
>(({ children, className, ...props }, ref) => (
  <Text
    variant="compact-description"
    ref={ref}
    className={cn("text-text-secondary", className)}
    {...props}
  >
    {children}
  </Text>
));
CardDescription.displayName = "CardDescription";

const CardContent = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => (
  <div ref={ref} className={cn("p-6 pt-0", className)} {...props} />
));
CardContent.displayName = "CardContent";

const CardFooter = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn("flex items-center gap-2 p-6 pt-0", className)}
    {...props}
  />
));
CardFooter.displayName = "CardFooter";

/**
 * A row in a divided card: 16px padding, and a hairline above every row but
 * the first, inset 16px from the left and running to the right edge.
 */
const CardRow = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn(
      "relative px-4 py-4",
      "not-first:before:absolute not-first:before:top-0 not-first:before:right-0 not-first:before:left-4 not-first:before:h-px not-first:before:bg-border-primary not-first:before:content-['']",
      className,
    )}
    {...props}
  />
));
CardRow.displayName = "CardRow";

export {
  Card,
  CardHeader,
  CardFooter,
  CardTitle,
  CardDescription,
  CardContent,
  CardRow,
};
